import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { DSDialog, DSHeading } from '@stihl-design-system/components';

import { SelectedImage } from './selected-image';
import { ImageViewerToolBar as ToolBar } from './tool-bar';

export type ViewerImageType = {
  id: string;
  url?: string;
  alt?: string;
  originalFilename?: string;
  isDeletable: boolean;
  isMainImage?: boolean;
};

export interface ImageViewerProps {
  dataTestId?: string;
  dialogIsOpen: boolean;
  closeDialog: () => void;
  title: string;
  initiallySelectedImage: ViewerImageType;
  images: ViewerImageType[];
  onDeleteImage: (imageId: string) => void;
  onSetMainImage?: (imageId: string) => void;
}

export const ImageViewer: FC<ImageViewerProps> = ({
  initiallySelectedImage,
  dialogIsOpen,
  closeDialog,
  title,
  images,
  onDeleteImage,
  onSetMainImage,
}) => {
  const { t } = useTranslation('shared');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedImage, setSelectedImage] = useState<ViewerImageType>(initiallySelectedImage);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  useEffect(() => {
    setSelectedImage(images[currentImageIndex]);
  }, [currentImageIndex, images]);

  useEffect(() => {
    setCurrentImageIndex(images.findIndex((image) => image.id === initiallySelectedImage.id));
  }, [initiallySelectedImage]);

  const resetState = () => {
    setCurrentImageIndex(0);
    setSelectedImage(initiallySelectedImage);
    setShowDeleteConfirmation(false);
  };

  const _onDeleteImage = () => {
    setShowDeleteConfirmation(false);
    if (currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1);
    } else if (images.length === 1) {
      closeDialog();
    }
    onDeleteImage(selectedImage.id);
  };

  const onCloseDialog = () => {
    closeDialog();
    resetState();
  };

  return (
    <DSDialog
      isOpen={dialogIsOpen}
      onClose={onCloseDialog}
      disableBackdropClose={true}
      aria-label={`${t('shared_imageViewer_title_aria')} (${selectedImage?.originalFilename})`}
      className="relative w-screen"
      header={<DSHeading tag="h3">{t(title)}</DSHeading>}
      closeButtonProps={{ 'data-testid': 'close-modal' }}
      footer={
        <ToolBar
          showDeleteConfirmation={showDeleteConfirmation}
          onSetMainImage={onSetMainImage}
          onDeleteImage={_onDeleteImage}
          setShowDeleteConfirmation={(show) => setShowDeleteConfirmation(show)}
          images={images}
          currentImageIndex={currentImageIndex}
          setCurrentImageIndex={setCurrentImageIndex}
          selectedImage={selectedImage}
        />
      }
      data-testid="image-viewer"
    >
      <SelectedImage index={currentImageIndex} image={selectedImage} />
    </DSDialog>
  );
};
